import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { isArray } from "lodash";

import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  BarChart3,
  Pause,
  Play,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  CircleDot,
  Settings,
} from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";
import { useDate } from "../../hooks/useDate";
import useTimezone from "../../hooks/useTimezone";
import usePlans from "../../hooks/usePlans";
import toastError from "../../errors/toastError";

import CampaignModal from "../../components/CampaignModal";
import CampaignsConfig from "../CampaignsConfig";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const idx = state.findIndex((u) => u.id === campaign.id);
        if (idx !== -1) state[idx] = campaign;
        else newCampaigns.push(campaign);
      });
    }
    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const idx = state.findIndex((u) => u.id === campaign.id);
    if (idx !== -1) {
      state[idx] = campaign;
      return [...state];
    }
    return [campaign, ...state];
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    const idx = state.findIndex((u) => u.id === campaignId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }

  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      boxSizing: "border-box",
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    configBtn: {
      width: 38,
      height: 38,
      borderRadius: 10,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e5e7eb")
      }`,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#ffffff"),
      "&:hover": {
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        background: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },

    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${
          dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")
        }`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
        whiteSpace: "nowrap",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${
          dsColors.border || (isDark ? "#202632" : "#e8e8e8")
        }`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
        whiteSpace: "nowrap",
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor:
          dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },

    nameCell: { fontWeight: 600 },
    refChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    notDefined: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontStyle: "italic",
    },

    statusChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    statusInactive: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    statusScheduled: {
      background: "rgba(99,102,241,0.12)",
      color: theme.palette.primary.main,
    },
    statusInProgress: {
      background: "rgba(245,158,11,0.12)",
      color: "#f59e0b",
    },
    statusFinished: {
      background: "rgba(16,185,129,0.12)",
      color: "#10b981",
    },
    statusCancelled: {
      background: "rgba(229,57,53,0.12)",
      color: "#e53935",
    },

    boolBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
    },
    boolYes: {
      background: "rgba(16,185,129,0.12)",
      color: "#10b981",
    },
    boolNo: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },

    actionsCell: {
      whiteSpace: "nowrap",
      "& > button": { marginLeft: 4 },
      "& > button:first-child": { marginLeft: 0 },
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    runBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#10b981",
      "&:hover": { background: "rgba(16,185,129,0.10)" },
    },
    pauseBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#f59e0b",
      "&:hover": { background: "rgba(245,158,11,0.10)" },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontSize: 13.5,
    },
  };
});

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { ticketSearchParam, clear } = useSearch();
  const searchParam = (ticketSearchParam || "").toLowerCase();

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  const { getCompanyTimezone } = useTimezone();
  const [companyTimezone, setCompanyTimezone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const tzData = await getCompanyTimezone(user.companyId);
        setCompanyTimezone(tzData.timezone);
      } catch {}
    })();
  }, [user.companyId]);

  useEffect(() => {
    (async () => {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useCampaigns) {
        toast.error(i18n.t("campaigns.noPermission"));
        setTimeout(() => history.push(`/`), 1000);
      }
    })();
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => fetchCampaigns(), 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user, socket]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setPageNumber(1);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const statusMeta = (val) => {
    switch (val) {
      case "PROGRAMADA":
        return {
          label: i18n.t("campaigns.status.scheduled"),
          chip: classes.statusScheduled,
          Icon: Clock,
        };
      case "EM_ANDAMENTO":
        return {
          label: i18n.t("campaigns.status.inProgress"),
          chip: classes.statusInProgress,
          Icon: CircleDot,
        };
      case "FINALIZADA":
        return {
          label: i18n.t("campaigns.status.finished"),
          chip: classes.statusFinished,
          Icon: CheckCircle2,
        };
      case "CANCELADA":
        return {
          label: i18n.t("campaigns.status.cancelled"),
          chip: classes.statusCancelled,
          Icon: XCircle,
        };
      case "INATIVA":
      default:
        return {
          label: i18n.t("campaigns.status.inactive"),
          chip: classes.statusInactive,
          Icon: AlertCircle,
        };
    }
  };

  const renderStatusChip = (val) => {
    const meta = statusMeta(val);
    const Icon = meta.Icon;
    return (
      <span className={`${classes.statusChip} ${meta.chip}`}>
        <Icon size={13} strokeWidth={2.2} />
        {meta.label}
      </span>
    );
  };

  const renderBool = (val) =>
    val ? (
      <span className={`${classes.boolBadge} ${classes.boolYes}`}>
        {i18n.t("campaigns.common.enabled")}
      </span>
    ) : (
      <span className={`${classes.boolBadge} ${classes.boolNo}`}>
        {i18n.t("campaigns.common.disabled")}
      </span>
    );

  const renderRef = (text) =>
    text ? (
      <span className={classes.refChip}>{text}</span>
    ) : (
      <span className={classes.notDefined}>
        {i18n.t("campaigns.common.notDefined")}
      </span>
    );

  const renderDate = (val, emptyKey) =>
    val ? (
      datetimeToClient(val, companyTimezone)
    ) : (
      <span className={classes.notDefined}>{i18n.t(emptyKey)}</span>
    );

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}

      <CampaignsConfig
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
      />

      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Megaphone size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("campaigns.title")}
              <span className={classes.countChip}>{campaigns.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("campaigns.subtitle", {
                defaultValue:
                  "Crie e acompanhe envios programados para listas de contatos.",
              })}
            </div>
          </div>
        </div>

        <div className={classes.headerActions}>
          <IconButton
            className={classes.configBtn}
            onClick={() => setConfigModalOpen(true)}
            title={i18n.t("campaigns.subMenus.settings")}
          >
            <Settings size={17} strokeWidth={1.8} />
          </IconButton>
          <Button className={classes.newBtn} onClick={handleOpenCampaignModal}>
            <Plus size={16} strokeWidth={2} />
            {i18n.t("campaigns.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <div className={classes.tableScroll} onScroll={handleScroll}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="left">
                  {i18n.t("campaigns.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.status")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.contactList")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.whatsapp")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.scheduledAt")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.completedAt")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("campaigns.table.confirmation")}
                </TableCell>
                <TableCell align="right">
                  {i18n.t("campaigns.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && campaigns.length === 0 ? (
                <TableRowSkeleton columns={8} />
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className={classes.emptyState}>
                    {searchParam
                      ? i18n.t("campaigns.empty.search", {
                          defaultValue:
                            "Nenhuma campanha encontrada para a busca.",
                        })
                      : i18n.t("campaigns.empty.list", {
                          defaultValue: "Nenhuma campanha cadastrada.",
                        })}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {campaigns.map((campaign) => (
                    <TableRow
                      key={campaign.id}
                      className={classes.tableRow}
                    >
                      <TableCell align="left" className={classes.nameCell}>
                        {campaign.name}
                      </TableCell>
                      <TableCell align="center">
                        {renderStatusChip(campaign.status)}
                      </TableCell>
                      <TableCell align="center">
                        {renderRef(
                          campaign.contactListId
                            ? campaign.contactList?.name
                            : null
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {renderRef(
                          campaign.whatsappId ? campaign.whatsapp?.name : null
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {renderDate(
                          campaign.scheduledAt,
                          "campaigns.common.noSchedule"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {renderDate(
                          campaign.completedAt,
                          "campaigns.common.notCompleted"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {renderBool(!!campaign.confirmation)}
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        {campaign.status === "EM_ANDAMENTO" && (
                          <IconButton
                            size="small"
                            className={classes.pauseBtn}
                            onClick={() => cancelCampaign(campaign)}
                            title={i18n.t("campaigns.buttons.stopCampaign")}
                          >
                            <Pause size={16} strokeWidth={1.8} />
                          </IconButton>
                        )}
                        {campaign.status === "CANCELADA" && (
                          <IconButton
                            size="small"
                            className={classes.runBtn}
                            onClick={() => restartCampaign(campaign)}
                            title={i18n.t("campaigns.buttons.restartCampaign", {
                              defaultValue: "Reiniciar campanha",
                            })}
                          >
                            <Play size={16} strokeWidth={1.8} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() =>
                            history.push(`/campaign/${campaign.id}/report`)
                          }
                          title={i18n.t("campaigns.buttons.report", {
                            defaultValue: "Relatório",
                          })}
                        >
                          <BarChart3 size={16} strokeWidth={1.8} />
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => handleEditCampaign(campaign)}
                          aria-label="edit"
                        >
                          <Pencil size={16} strokeWidth={1.8} />
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.deleteActionBtn}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingCampaign(campaign);
                          }}
                          aria-label="delete"
                        >
                          <Trash2 size={16} strokeWidth={1.8} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={8} />}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </div>
  );
};

export default Campaigns;
